// Permission-based nav visibility hook.
// Replaces the old static role→section map (ROLE_NAV_ACCESS).
//
// A nav item is shown when the logged-in admin has ANY of its `permissions` codes.
// An empty (or absent) permissions array means the item is always visible.
// super_admin bypasses all checks (mirrors backend require_permission behaviour).
// While permissions are still loading (null) all items stay visible to avoid flash.

import { useCallback } from 'react';
import { useAuthStore } from '@/stores/auth.store';
import { checkAnyPermission } from '@/lib/auth/permissions';

export interface NavItem {
  label: string;
  href: string;
  icon: string;
  permissions?: string[];
}

export interface NavSection {
  group: string;
  items: NavItem[];
}

export function useNavAccess() {
  const admin = useAuthStore((s) => s.admin);
  const permissions = useAuthStore((s) => s.permissions);
  const isLoading = admin?.role !== 'super_admin' && permissions === null;

  const canSeeItem = useCallback(
    (item: NavItem): boolean => {
      if (!admin) return false;
      if (admin.role === 'super_admin') return true;
      if (isLoading) return true; // avoid sidebar flash while fetching
      if (!item.permissions || item.permissions.length === 0) return true;
      return checkAnyPermission(admin.role, permissions, item.permissions);
    },
    [admin, permissions, isLoading],
  );

  const getVisibleItems = useCallback(
    (section: NavSection): NavItem[] => section.items.filter(canSeeItem),
    [canSeeItem],
  );

  const canSeeSection = useCallback(
    (section: NavSection): boolean => getVisibleItems(section).length > 0,
    [getVisibleItems],
  );

  return { canSeeItem, getVisibleItems, canSeeSection };
}
